'use client';

import { InfoCircledIcon } from '@radix-ui/react-icons';
import Link from 'next/link';
import { useState } from 'react';
import { ArrowIcon } from '@/icons';
import type { Area } from '@/types/booliApi';
import { useStatisticsQuery } from '@/utils/statisticsQuery/hooks/useStatisticsQuery';
import { TimeChart } from '../Charts/XYCharts/LineChart/variants/Time';
import { Button } from '../ui/button';
import { VisualMenu } from '../Visualization/VisualMenu';
import AreaTabs from './AreaTabs';

export type PreviewIndices = {
  series: 'apartmentpriceindex' | 'housepriceindex';
  start: number;
  ids: number;
};

interface PreviewPriceIndexProps {
  areas: Area[];
}
const PreviewPriceIndex = ({ areas }: PreviewPriceIndexProps) => {
  const [indices, setIndices] = useState<PreviewIndices>({
    series: 'apartmentpriceindex',
    start: 2013,
    ids: 973576,
  });
  const { clientQuery } = useStatisticsQuery({
    resource: 'areas',
    series: [indices.series],
    period: { start: `${indices.start}-01-01`, granularity: 'month' },
    ids: [indices.ids],
    isNewConstruction: 0,
  });

  const { isLoading, isError } = clientQuery.useBaseQuery();

  if (isError) {
    return null;
  }

  const chartData = clientQuery.formatToChart()?.at(0) || [];

  return (
    <div className='mobile:m-4 grid grid-cols-1 place-items-center gap-y-16'>
      <div className='flex flex-col items-center'>
        <div>
          <h2 className='mobile:text-lg text-center text-xl'>SBAB Booli Housing Price Index (HPI)</h2>
          <p className='tablet:text-3xl mobile:text-2xl mt-1 text-center text-4xl font-semibold'>
            Håll koll på prisutvecklingen
          </p>
          <p className='mt-4 max-w-2xl text-center'>
            Med hjälp av vårt index kan du se prisutvecklingen på bostadsmarknaden. På vår prisindexsida kan du bland
            annat jämföra olika områden över tid och läsa mer om hur vår prisindexmodell fungerar.
          </p>
        </div>
        <Link href='/indices'>
          <Button
            className='mt-10'
            variant={'outline'}
          >
            Gå till prisindex <ArrowIcon className='ml-4' />
          </Button>
        </Link>
      </div>
      <div className='w-full'>
        <AreaTabs
          areas={areas}
          indices={indices}
          setIndices={setIndices}
        />
        <div className='flex w-full justify-end'>
          <div className='bg-bui-color-light-gray rounded-full p-1'>
            <button
              type='button'
              className={`rounded-full px-3 py-2 ${
                indices.series === 'apartmentpriceindex'
                  ? 'bg-bui-color-orange text-bui-color-white'
                  : 'bg-bui-color-light-gray'
              }`}
              onClick={() => setIndices((prev) => ({ ...prev, series: 'apartmentpriceindex' }))}
            >
              Lägenhet
            </button>
            <button
              type='button'
              className={`rounded-full px-8 py-2 ${
                indices.series === 'housepriceindex'
                  ? 'bg-bui-color-orange text-bui-color-white'
                  : 'bg-bui-color-light-gray'
              }`}
              onClick={() => setIndices((prev) => ({ ...prev, series: 'housepriceindex' }))}
            >
              Hus
            </button>
          </div>
        </div>
        <div className='mt-4 w-full'>
          <VisualMenu
            title='Prisindex'
            description='Hedoniskt TimeDummy-index'
            isError={isError}
            isLoading={isLoading}
          >
            {({ ref, showAnnotations }) => (
              <TimeChart
                ref={ref}
                //@ts-expect-error
                data={[chartData[indices.series] || []]}
                isLoading={isLoading}
                isError={isError}
                showAnnotations={showAnnotations}
              />
            )}
          </VisualMenu>
        </div>
        <p className='mt-4 flex w-full items-center justify-end opacity-50'>
          <span>
            <InfoCircledIcon className='mr-1 h-4 w-4' />
          </span>
          Grafen visare den senaste tidens prisutveckling för området.
        </p>
      </div>
    </div>
  );
};

export default PreviewPriceIndex;
